import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  Message,
  WelcomeElement,
} from '@components';
import { blogCategoriesQuery, siteQuery } from '@queries';
import { ChevronRightIcon, SearchIcon } from '@svgs';

import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';
import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';

import CategoryRow from './CategoryRow/CategoryRow.jsx';

const matchCategory = (category, query) => {
  // determine filter match for a particular Category

  // explicit matching only
  const exactMatchFields = [category.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const categoryFields = [category.name];
  if (categoryFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

const filterCategories = (categories, query) => {
  // client side filtering of categories
  const cleanedQuery = query.toLowerCase().trim();
  return categories.filter((category) => {
    if (matchCategory(category, cleanedQuery)) {
      return category;
    }
  });
};

export const BlogCategories = () => {
  const { t, i18n } = useTranslation();
  const { siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // filter search term
  const [searchTerm, setSearchTerm] = useState('');

  // get site
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const {
    data: categoriesData,
    isPending: categoriesIsPending,
    isError,
  } = useQuery(
    blogCategoriesQuery({
      limit: 100,
      order_by: 'name',
      site_id: siteID,
    }),
  );

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);
  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const filteredCategories = searchTerm
    ? filterCategories(categoriesData.objects, searchTerm)
    : categoriesData?.objects;

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <Link to={`/dashboard/sites/${siteID}/blogs`}>Blogs</Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Blog categories')}</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Blog categories')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/sites/${siteID}/blogs/categories/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={categoriesIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right"></div>
            </div>
            {categoriesIsPending && (
              <Loader className="sb-loader sb-loader-large" />
            )}
            {!categoriesIsPending &&
              (isError || categoriesData.objects?.length == 0) && (
                <WelcomeElement>
                  <p>No blog categories under this website</p>
                  <Link
                    to={`/dashboard/sites/${siteID}/blogs/categories/new`}
                    className="sb-btn sb-btn-save"
                  >
                    Add first category
                  </Link>
                </WelcomeElement>
              )}
            {!categoriesIsPending && !isError && (
              <div className="sb-content-view-row">
                {filteredCategories.map((category) => {
                  return <CategoryRow key={category.id} category={category} />;
                })}
              </div>
            )}
            {!categoriesIsPending && searchTerm && (
              <Message
                messageStyle="notice"
                title={
                  `Filtered to ${filteredCategories.length} of ${categoriesData.objects.length} category` +
                  (categoriesData.objects.length != 1 ? 's' : '')
                }
                fadeoutMessage={false}
                isFormMessage={false}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
